import React, {useMemo} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch} from 'react-redux';
import {Formik} from 'formik';
import {isEqual} from 'lodash';
import moment from 'moment';
import {useInvoiceOrdersUIContext} from '../InvoiceOrdersUIContext';
import FiltersBar from '../../../_common/components/FiltersBar';
import {FilterFromDate, FilterToDate} from '../../../_common/components/FilterDateRange';
import FilterTextField from '../../../_common/components/FilterTextField';
import FilterSelect from '../../../_common/components/FilterSelect';
import {statusIDFilterOptions} from '../../../_common/constants/options';
import * as invoiceOrdersActions from '../../_redux/invoiceOrdersActions';
import {RequestCancellationManager} from '../../../_common/utils/RequestCancellationManager';
import {toAbsoluteUrl} from '../../../../../_metronic/_helpers';
import {Button} from 'react-bootstrap';
import classNames from 'classnames';
import styles from './styles.module.scss';

const prepareFilter = (queryParams, values) => {
  const {name, agentID, customerName, fromDate, toDate, statusID, customerID, sum} = values;
  const newQueryParams = {...queryParams};
  const filter = {};
  if (Boolean(name)) {
    filter.name = name;
  } else {
    delete newQueryParams.name;
  }
  if (Boolean(agentID)) {
    filter.agentID = agentID;
  } else {
    delete newQueryParams.agentID;
  }
  if (Boolean(customerName)) {
    filter.customerName = customerName;
  } else {
    delete newQueryParams.customerName;
  }
  if (Boolean(fromDate)) {
    filter.fromDate = moment(fromDate).format('YYYY-MM-DD');
  } else {
    delete newQueryParams.fromDate;
  }
  if (Boolean(toDate)) {
    filter.toDate = moment(toDate).add(1, 'days').format('YYYY-MM-DD');
  } else {
    delete newQueryParams.toDate;
  }
  if (Boolean(statusID)) {
    filter.statusID = statusID;
  } else {
    delete newQueryParams.statusID;
  }
  if (Boolean(customerID)) {
    filter.customerID = customerID;
  } else {
    delete newQueryParams.customerID;
  }
  if (Boolean(sum)) {
    filter.sum = sum;
  } else {
    delete newQueryParams.sum;
  }
  return {...newQueryParams, ...filter};
};

export function InvoiceOrdersFilter() {
  const invoiceOrdersUIContext = useInvoiceOrdersUIContext();
  const invoiceOrdersUIProps = useMemo(() => {
    return {
      queryParams: invoiceOrdersUIContext.queryParams,
      setQueryParams: invoiceOrdersUIContext.setQueryParams,
    };
  }, [invoiceOrdersUIContext]);

  const dispatch = useDispatch();

  const applyFilter = (values) => {
    RequestCancellationManager.stopRequests(invoiceOrdersActions.getInvoiceOrdersListIDPrefix);

    const newQueryParams = prepareFilter(invoiceOrdersUIProps.queryParams, values);
    if (!isEqual(newQueryParams, invoiceOrdersUIProps.queryParams)) {
      invoiceOrdersUIProps.setQueryParams(newQueryParams);
    }
  };

  const {queryParams} = useInvoiceOrdersUIContext();
  const updateTables = async () => {
    await dispatch(invoiceOrdersActions.getInvoiceOrdersConfirmCustomers(queryParams));
    await dispatch(invoiceOrdersActions.getInvoiceOrdersEditAndSend(queryParams));
  };

  const intl = useIntl();
  const prefix = 'invoice-orders-filter';

  const qs = new URLSearchParams(window.location.search);
  let name = qs.get('name') || '';
  let agentID = qs.get('agentID') || '';
  const customerName = qs.get('customerName') || '';
  const fromDate = qs.get('fromDate') || '';
  const toDate = Boolean(qs.get('toDate'))
    ? moment(qs.get('toDate')).subtract(1, 'days').toDate()
    : '';
  const statusID = qs.get('statusID') || '';
  const customerID = qs.get('customerID') || '';
  const sum = qs.get('sum') || '';
  const containerClassnames = 'w-200px position-relative my-1 px-3';

  return (
    <Formik
      initialValues={{
        name,
        agentID,
        customerName,
        fromDate,
        toDate,
        statusID,
        customerID,
        sum,
      }}
      onSubmit={(values) => applyFilter(values)}
      enableReinitialize={true}
    >
      {({values, ...formikProps}) => (
        <FiltersBar
          filters={
            <div className={'d-flex flex-wrap justify-content-center align-items-center mb-0 w-100'}>
              <FilterTextField
                fieldName="name"
                placeholder={intl.formatMessage({id: `${prefix}-name`, defaultMessage: 'Name'})}
                value={values.name}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="agentID"
                placeholder={intl.formatMessage({id: `${prefix}-agentId`, defaultMessage: 'Agent Id',})}
                value={values.agentID}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="customerName"
                placeholder={intl.formatMessage({id: `${prefix}-customerName`, defaultMessage: 'Customer name',})}
                value={values.customerName}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="customerID"
                placeholder={intl.formatMessage({id: `${prefix}-customerID`, defaultMessage: 'Customer ID',})}
                value={values.customerID}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterFromDate
                values={values}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterToDate
                values={values}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="sum"
                placeholder={intl.formatMessage({id: `${prefix}-sum`, defaultMessage: 'Sum',})}
                value={values.sum}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterSelect
                fieldName={'statusID'}
                valueName={values.statusID}
                options={statusIDFilterOptions}
                placeholder={intl.formatMessage({id: `${prefix}-statusID`, defaultMessage: 'Status type',})}
                containerStyles={containerClassnames}
                {...formikProps}
              />
            </div>
          }
          buttons={
            <Button
              type="button"
              className={classNames(styles.gridRefreshButton, `btn btm-sm p-0`)}
              onClick={() => updateTables()}
            >
              <img
                src={toAbsoluteUrl('/media/svg/Update_white.svg')}
                alt="send"
                height={24}
              />
            </Button>
          }
        />
      )}
    </Formik>
  );
}
